import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import UpdateIcon from '@mui/icons-material/Update';
import styles from './StageEvaluationPage.module.css';
import {
    fetchEtapasStatus, fetchEtapaRespostas, saveEtapaRespostas, saveVeredito,
    EtapaInfo, VereditoInfo,
    adminGerenciarEtapa,
} from './services/avaliacaoAPI';
import { PERGUNTAS, ETAPA_LABELS, ETAPA_ORDEM, ADERENCIA_PISOS_LIST, ADERENCIA_OPCOES_LIST, Pergunta } from './perguntas';
import { useAuthStore } from '../../stores/authStore';

const NOTAS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const StageEvaluationPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const pedidoId = parseInt(id || '0', 10);
    const navigate = useNavigate();

    const [etapas, setEtapas] = useState<EtapaInfo[]>([]);
    const [veredito, setVeredito] = useState<VereditoInfo | null>(null);
    const [etapaAtiva, setEtapaAtiva] = useState<number | string>(10);
    const [respostas, setRespostas] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [adminMsg, setAdminMsg] = useState('');

    const { user } = useAuthStore();
    const isAdmin = user?.role === 'admin' || user?.role === 'supervisor' || user?.role === 'diretor';
    const isAws = user?.isAws === true;
    // On-premise: readonly (visualiza respostas). AWS: pode preencher.
    const isReadonlyView = !isAws;

    const handleAdminAction = async (etapa: number, action: 'liberar' | 'postergar') => {
        const dias = action === 'postergar' ? parseInt(prompt('Quantos dias postergar?') || '0', 10) : undefined;
        if (action === 'postergar' && (!dias || dias <= 0)) return;
        try {
            const res = await adminGerenciarEtapa(pedidoId, etapa, action, dias);
            setAdminMsg(res.message);
            await loadStatus();
            setTimeout(() => setAdminMsg(''), 4000);
        } catch (e: any) {
            setError(e?.response?.data?.error || 'Erro ao gerenciar etapa.');
        }
    };

    // ── Veredito state ──
    const [vereditoValor, setVereditoValor] = useState<'aprovado' | 'reprovado'>('aprovado');
    const [negociacaoPreco, setNegociacaoPreco] = useState<'sim' | 'nao'>('nao');
    const [canalVenda, setCanalVenda] = useState('');
    const [nomeRevenda, setNomeRevenda] = useState('');

    useEffect(() => {
        loadStatus();
    }, [pedidoId]);

    // Carregar respostas salvas da etapa ativa (apenas etapas numéricas)
    useEffect(() => {
        if (typeof etapaAtiva !== 'number') return;
        const loadRespostas = async () => {
            try {
                const data = await fetchEtapaRespostas(pedidoId, etapaAtiva);
                setRespostas(data.respostas || {});
            } catch (e) {
                setRespostas({});
            }
        };
        loadRespostas();
    }, [etapaAtiva, pedidoId]);

    const loadStatus = async () => {
        try {
            setLoading(true);
            const data = await fetchEtapasStatus(pedidoId);
            setEtapas(data.etapas);
            setVeredito(data.veredito);
            if (data.veredito) {
                setVereditoValor(data.veredito.veredito);
                setNegociacaoPreco(data.veredito.negociacao_preco || 'nao');
                setCanalVenda(data.veredito.canal_venda || '');
                setNomeRevenda(data.veredito.nome_revenda || '');
            }
        } catch (e: any) {
            setError('Erro ao carregar etapas.');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveEtapa = async () => {
        if (typeof etapaAtiva !== 'number') return;
        const perguntasEtapa = PERGUNTAS.filter(p => p.etapa === etapaAtiva && p.tipo !== 'texto');
        const hasAnyAnswer = perguntasEtapa.some(p => {
            const val = respostas[p.chave];
            return val !== undefined && val !== null && val !== '' && !(Array.isArray(val) && val.length === 0);
        });

        if (!hasAnyAnswer) {
            setError('Responda ao menos uma pergunta antes de salvar.');
            return;
        }

        setSaving(true);
        setError('');
        try {
            await saveEtapaRespostas(pedidoId, etapaAtiva as number, respostas);
            await loadStatus();
        } catch (e: any) {
            setError('Erro ao salvar respostas.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveVeredito = async () => {
        setSaving(true);
        setError('');
        try {
            const res = await saveVeredito(pedidoId, { veredito: vereditoValor, negociacao_preco: negociacaoPreco, canal_venda: canalVenda, nome_revenda: nomeRevenda });
            setAdminMsg(res?.data?.message || 'Veredito salvo com sucesso.');
            await loadStatus();
            setTimeout(() => setAdminMsg(''), 4000);
        } catch (e: any) {
            setError(e?.response?.data?.error || 'Erro ao salvar veredito.');
        } finally {
            setSaving(false);
        }
    };

    const handleResposta = (chave: string, valor: any, tipo: string) => {
        setRespostas(prev => {
            if (tipo === 'multipla') {
                const atual: string[] = Array.isArray(prev[chave]) ? [...prev[chave]] : [];
                const idx = atual.indexOf(valor);
                if (idx >= 0) atual.splice(idx, 1);
                else atual.push(valor);
                return { ...prev, [chave]: atual };
            }
            return { ...prev, [chave]: valor };
        });
    };

    const getEtapaStatus = (etapa: number | string): string => {
        // Veredito: disponível se todas as etapas foram respondidas; respondido se já salvo
        if (etapa === 'veredito') {
            if (veredito) return 'respondida';
            const todasRespondidas = ETAPA_ORDEM
                .filter(e => typeof e === 'number')
                .every(e => {
                    const et = etapas.find(x => x.etapa === e);
                    return et?.status === 'respondida';
                });
            return todasRespondidas ? 'disponivel' : 'aguardando';
        }
        const e = etapas.find(x => x.etapa === etapa);
        if (!e) return 'aguardando';
        // Confia no status calculado pelo backend (já aplica lógica sequencial)
        if (e.status === 'respondida') return 'respondida';
        if (e.status === 'disponivel') return 'disponivel';
        return 'aguardando';
    };

    const perguntasEtapa = typeof etapaAtiva === 'number' ? PERGUNTAS.filter(p => p.etapa === etapaAtiva) : [];
    const statusAtual = getEtapaStatus(etapaAtiva);
    const isReadonly = statusAtual === 'aguardando' || statusAtual === 'respondida' || isReadonlyView;
    const isVereditoReadonly = etapaAtiva === 'veredito' && (statusAtual === 'respondida' || isReadonlyView);

    const renderPergunta = (pq: Pergunta) => {
        const val = respostas[pq.chave];

        switch (pq.tipo) {
            case 'unica':
                return (
                    <div className={styles.opcoesGrid}>
                        {pq.opcoes.map(op => (
                            <label key={op} className={`${styles.opcaoBtn} ${val === op ? styles.opcaoAtiva : ''}`}>
                                <input type="radio" name={pq.chave} value={op} checked={val === op}
                                    onChange={() => handleResposta(pq.chave, op, 'unica')} disabled={isReadonly} />
                                {op}
                            </label>
                        ))}
                    </div>
                );

            case 'multipla':
                return (
                    <div className={styles.opcoesGrid}>
                        {pq.opcoes.map(op => {
                            const arr = Array.isArray(val) ? val : [];
                            const checked = arr.includes(op);
                            return (
                                <label key={op} className={`${styles.opcaoBtn} ${checked ? styles.opcaoAtiva : ''}`}>
                                    <input type="checkbox" checked={checked}
                                        onChange={() => handleResposta(pq.chave, op, 'multipla')} disabled={isReadonly} />
                                    {op}
                                </label>
                            );
                        })}
                    </div>
                );

            case 'texto':
                return (
                    <textarea className={styles.textarea}
                        placeholder="Descreva aqui..."
                        value={typeof val === 'string' ? val : ''}
                        onChange={e => handleResposta(pq.chave, e.target.value, 'texto')}
                        disabled={isReadonly}
                        rows={3}
                    />
                );

            case 'nota_0_10':
                return (
                    <div className={styles.notaGrid}>
                        {NOTAS.map(n => (
                            <button key={n} type="button"
                                className={`${styles.notaBtn} ${val === n ? styles.notaAtiva : ''}`}
                                onClick={() => handleResposta(pq.chave, n, 'unica')}
                                disabled={isReadonly}
                            >{n}</button>
                        ))}
                    </div>
                );

            case 'tabela_aderencia':
                return (
                    <div className={styles.tabelaAderencia}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Tipo de piso</th>
                                    {ADERENCIA_OPCOES_LIST.map(op => <th key={op}>{op}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {ADERENCIA_PISOS_LIST.map(piso => {
                                    const pisoData = val?.[piso] || [];
                                    return (
                                        <tr key={piso}>
                                            <td>{piso}</td>
                                            {ADERENCIA_OPCOES_LIST.map(op => {
                                                const checked = pisoData.includes(op);
                                                return (
                                                    <td key={op} className={`${styles.tdCheck} ${checked ? styles.tdCheckAtiva : ''}`}>
                                                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', cursor: 'pointer' }}>
                                                            <input type="checkbox" checked={checked}
                                                                onChange={() => {
                                                                    const atual = Array.isArray(val?.[piso]) ? [...val[piso]] : [];
                                                                    const idx = atual.indexOf(op);
                                                                    if (idx >= 0) atual.splice(idx, 1);
                                                                    else atual.push(op);
                                                                    handleResposta(pq.chave, { ...(val || {}), [piso]: atual }, 'unica');
                                                                }}
                                                                disabled={isReadonly}
                                                            />
                                                        </label>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                );

            default:
                return null;
        }
    };

    if (loading) return <div className={styles.container}><p className={styles.loading}>Carregando...</p></div>;

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate(`/amostras-pedidos/${pedidoId}`)}>
                    <ArrowBackIcon fontSize="small" /> Voltar
                </button>
                <h1>Avaliação em Etapas — Pedido #{pedidoId}</h1>
            </div>

            {error && <p className={styles.errorMsg}>{error}</p>}

            {/* Progresso */}
            <div className={styles.progressBar}>
                {ETAPA_ORDEM.map(etapa => {
                    const st = getEtapaStatus(etapa);
                    const isActive = etapa === etapaAtiva;
                    const isVeredito = etapa === 'veredito';
                    return (
                        <button key={String(etapa)}
                            className={`${styles.progressStep} ${st === 'respondida' ? styles.stepDone : ''} ${st === 'disponivel' ? styles.stepReady : ''} ${isActive ? styles.stepCurrent : ''} ${isVeredito ? styles.stepVeredito : ''}`}
                            onClick={() => setEtapaAtiva(etapa)}
                            disabled={isVeredito && st === 'aguardando'}
                            title={isVeredito ? 'Veredito Final' : `${etapa} dias`}
                        >
                            <span className={styles.stepNum}>{isVeredito ? 'FIM' : `${etapa}d`}</span>
                            <span className={styles.stepLabel}>
                                {st === 'respondida' ? '✓' : st === 'disponivel' ? '●' : '○'}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Admin: gerenciar etapas */}
            {isAdmin && (
                <div className={styles.adminBar}>
                    <div className={styles.adminHeader}>
                        <SettingsIcon fontSize="small" />
                        <span>ADMIN</span>
                    </div>
                    <div className={styles.adminActions}>
                        {ETAPA_ORDEM.filter(e => typeof e === 'number').map(etapa => {
                            const st = getEtapaStatus(etapa as number);
                            return (
                                <div key={String(etapa)} className={styles.adminEtapaGroup}>
                                    <span className={`${styles.adminEtapaBadge} ${st === 'respondida' ? styles.adminEtapaDone : st === 'disponivel' ? styles.adminEtapaOpen : ''}`}>
                                        {etapa}d
                                    </span>
                                    <button
                                        className={styles.adminBtnLiberar}
                                        onClick={() => handleAdminAction(etapa as number, 'liberar')}
                                        title={`Liberar etapa ${etapa}d — abre a janela agora`}
                                    >
                                        <LockOpenIcon sx={{ fontSize: 14 }} />
                                    </button>
                                    <button
                                        className={styles.adminBtnPostergar}
                                        onClick={() => handleAdminAction(etapa as number, 'postergar')}
                                        title={`Postergar etapa ${etapa}d — adia a janela`}
                                    >
                                        <UpdateIcon sx={{ fontSize: 14 }} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {adminMsg && <div style={{ padding: '8px 14px', borderRadius: 6, background: '#ecfdf5', color: '#059669', fontWeight: 600, marginBottom: 12, fontSize: '0.85rem' }}>{adminMsg}</div>}

            {/* Status da etapa atual */}
            <div className={styles.statusBar}>
                {etapaAtiva === 'veredito' ? (
                    <>
                        {statusAtual === 'aguardando' && <span className={styles.statusTag} style={{ background: '#fef3c7', color: '#b45309' }}>🔒 Responda todas as etapas (10d, 30d, 60d) primeiro</span>}
                        {statusAtual === 'disponivel' && <span className={styles.statusTag} style={{ background: '#eff6ff', color: '#2563eb' }}>📝 Disponível para registrar o veredito final</span>}
                        {statusAtual === 'respondida' && <span className={styles.statusTag} style={{ background: '#ecfdf5', color: '#059669' }}>✅ Veredito registrado — ficha finalizada</span>}
                    </>
                ) : (
                    <>
                        {statusAtual === 'aguardando' && <span className={styles.statusTag} style={{ background: '#fef3c7', color: '#b45309' }}>⏳ Janela ainda não aberta</span>}
                        {statusAtual === 'disponivel' && <span className={styles.statusTag} style={{ background: '#eff6ff', color: '#2563eb' }}>📝 Disponível para responder</span>}
                        {statusAtual === 'respondida' && <span className={styles.statusTag} style={{ background: '#ecfdf5', color: '#059669' }}>✅ Respondida</span>}
                    </>
                )}
            </div>

            {/* Formulário de perguntas (apenas etapas numéricas) */}
            {typeof etapaAtiva === 'number' && (
            <div className={styles.formCard}>
                <h2 className={styles.etapaTitle}>Etapa: {ETAPA_LABELS[etapaAtiva]}</h2>

                {perguntasEtapa.map((pq) => {
                    const isSubQuestion = pq.chave.endsWith('_desc');
                    return (
                    <div key={pq.chave} className={styles.perguntaBlock}>
                        <p className={styles.perguntaTexto}>
                            {!isSubQuestion && <span className={styles.perguntaNum}>{pq.ordem}.</span>} {pq.pergunta}
                        </p>
                        {pq.subtitulo && <p className={styles.perguntaSub}>{pq.subtitulo}</p>}
                        {renderPergunta(pq)}
                    </div>
                    );
                })}

                {!isReadonly && !isReadonlyView && (
                <div className={styles.actions}>
                    <button className={styles.btnPrimary} onClick={handleSaveEtapa} disabled={saving}>
                        {saving ? 'Salvando...' : 'Salvar Etapa'}
                    </button>
                </div>
                )}
            </div>
            )}

            {/* Veredito Final como aba própria */}
            {etapaAtiva === 'veredito' && (
                <div className={styles.formCard}>
                    <h2 className={styles.etapaTitle}>Veredito Final</h2>

                    {veredito && (
                        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 8, background: '#f0fdf4', color: '#166534', fontWeight: 600, fontSize: '0.85rem' }}>
                            ✅ Veredito já registrado. Esta ficha está finalizada e não pode mais ser alterada.
                        </div>
                    )}

                    <div className={styles.perguntaBlock}>
                        <p className={styles.perguntaTexto}>Resultado final do teste:</p>
                        <div className={styles.vereditoBtns}>
                            <button className={`${styles.vereditoBtn} ${vereditoValor === 'aprovado' ? styles.vereditoAprovado : ''}`}
                                onClick={() => !isVereditoReadonly && setVereditoValor('aprovado')}
                                disabled={isVereditoReadonly}
                                type="button"
                            >✅ APROVADO</button>
                            <button className={`${styles.vereditoBtn} ${vereditoValor === 'reprovado' ? styles.vereditoReprovado : ''}`}
                                onClick={() => !isVereditoReadonly && setVereditoValor('reprovado')}
                                disabled={isVereditoReadonly}
                                type="button"
                            >❌ REPROVADO</button>
                        </div>
                    </div>

                    <div className={styles.perguntaBlock}>
                        <p className={styles.perguntaTexto}>Já foi iniciada a negociação de preço?</p>
                        <div className={styles.opcoesGrid}>
                            <label className={`${styles.opcaoBtn} ${negociacaoPreco === 'sim' ? styles.opcaoAtiva : ''}`}>
                                <input type="radio" name="negpreco" checked={negociacaoPreco === 'sim'} onChange={() => setNegociacaoPreco('sim')} disabled={isVereditoReadonly} /> Sim
                            </label>
                            <label className={`${styles.opcaoBtn} ${negociacaoPreco === 'nao' ? styles.opcaoAtiva : ''}`}>
                                <input type="radio" name="negpreco" checked={negociacaoPreco === 'nao'} onChange={() => setNegociacaoPreco('nao')} disabled={isVereditoReadonly} /> Não
                            </label>
                        </div>
                    </div>

                    <div className={styles.perguntaBlock}>
                        <p className={styles.perguntaTexto}>Por qual canal será vendido?</p>
                        <input className={styles.textInput} value={canalVenda} onChange={e => setCanalVenda(e.target.value)} placeholder="Ex: E-commerce, Loja física..." disabled={isVereditoReadonly} />
                    </div>

                    <div className={styles.perguntaBlock}>
                        <p className={styles.perguntaTexto}>Se for revenda, qual nome?</p>
                        <input className={styles.textInput} value={nomeRevenda} onChange={e => setNomeRevenda(e.target.value)} placeholder="Nome da revenda" disabled={isVereditoReadonly} />
                    </div>

                    {!isVereditoReadonly && (
                    <div className={styles.actions}>
                        <button className={styles.btnPrimary} onClick={handleSaveVeredito} disabled={saving}>
                            {saving ? 'Salvando...' : 'Salvar Veredito'}
                        </button>
                    </div>
                    )}
                </div>
            )}

            {/* Navegação entre etapas */}
            <div className={styles.navEtapas}>
                {ETAPA_ORDEM.indexOf(etapaAtiva) > 0 && (
                    <button className={styles.btnSecondary} onClick={() => setEtapaAtiva(prev => {
                        const idx = ETAPA_ORDEM.indexOf(prev);
                        return ETAPA_ORDEM[idx - 1];
                    })}>← Etapa anterior</button>
                )}
                <div style={{ flex: 1 }} />
                {ETAPA_ORDEM.indexOf(etapaAtiva) < ETAPA_ORDEM.length - 1 && (
                    <button className={styles.btnPrimary} onClick={() => setEtapaAtiva(prev => {
                        const idx = ETAPA_ORDEM.indexOf(prev);
                        return ETAPA_ORDEM[idx + 1];
                    })}>Próxima etapa →</button>
                )}
            </div>
        </div>
    );
};

export default StageEvaluationPage;
